import { FinancialRecord } from '../financialRecords'
import type { FinancialRecordSummary } from '../types'
import type { GenerationResult } from './types'

export type AnnualFinalValues = Map<number, number>

// TODO(ZL-961): the retry contract enforced here is implicit — `_generate(year)`
// must be idempotent because `Calculator.calculateForYearWithDrawdowns` may
// call it many times for the same year while iterating drawdown recipients.
// ZL-957 was a bug where LiabilityGenerator broke that invariant. Long-term
// fix: replace the class hierarchy with pure functions (see ZL-961).
abstract class TimelineGenerator {
  private annualFinalValues: AnnualFinalValues = new Map()
  private currentYear: number

  constructor(protected readonly record: FinancialRecord) {
    this.setYearFinalValue(record.startAt.getFullYear(), record.annualValue)
    this.setYearFinalValue(record.startAt.getFullYear() - 1, record.annualValue)
    this.currentYear = record.startAt.getFullYear()
  }

  public generate(
    year: number,
    drawDownRecipients?: FinancialRecordSummary[]
  ): GenerationResult {
    this.currentYear = year
    const result = this._generate(year, drawDownRecipients)
    this.setYearFinalValue(year, result.finalValue)
    return result
  }

  // Subclasses MUST implement `_generate` idempotently: two consecutive calls
  // with the same `year` must produce the same result, and repeated calls must
  // not advance internal state. Read prior years via `previousYearFinalValue`;
  // do not track "current year" balances in mutable fields.
  // See ZL-961 for the planned pure-function refactor.
  abstract _generate(
    year: number,
    drawDownRecipients?: FinancialRecordSummary[]
  ): GenerationResult

  protected get previousYearFinalValue(): number {
    return this.annualFinalValues.get(this.currentYear - 1) || 0
  }

  private setYearFinalValue(year: number, value: number): void {
    this.annualFinalValues.set(year, value)
  }
}

export default TimelineGenerator
